import React, { useState, useEffect, useCallback } from "react";
import {
  Activity,
  Zap,
  Clock,
  DollarSign,
  BarChart3,
  TrendingUp,
  Hash,
  MessageSquare,
  RefreshCw,
  Filter,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const PERIOD_OPTIONS = [
  { value: "1m", label: "This Month" },
  { value: "2m", label: "Last Month" },
  { value: "3m", label: "Last 3 Months" },
  { value: "6m", label: "Last 6 Months" },
  { value: "all", label: "All Time" },
];

const CHANNEL_COLORS = {
  widget: { bg: "bg-blue-500", text: "text-blue-700", light: "bg-blue-100" },
  api: { bg: "bg-green-500", text: "text-green-700", light: "bg-green-100" },
  slack: {
    bg: "bg-purple-500",
    text: "text-purple-700",
    light: "bg-purple-100",
  },
  sms: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-100" },
  email: { bg: "bg-rose-500", text: "text-rose-700", light: "bg-rose-100" },
};

/**
 * Calculate estimated cost using gpt-4o-mini pricing.
 * Input: $0.15 / 1M tokens, Output: $0.60 / 1M tokens
 */
const calcCost = (tokensIn, tokensOut) => {
  const inCost = ((tokensIn || 0) * 0.00015) / 1000;
  const outCost = ((tokensOut || 0) * 0.0006) / 1000;
  return inCost + outCost;
};

const fmt = (n) => (n != null ? Number(n).toLocaleString() : "0");

const fmtCost = (n) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Metric Card ───────────────────────────────────────────────────

const MetricCard = ({ icon: Icon, iconColor, label, value, subtext }) => (
  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </span>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div className="text-2xl font-bold text-slate-800">{value}</div>
    {subtext && <div className="text-xs text-slate-400 mt-1">{subtext}</div>}
  </div>
);

// ─── Channel Bar ───────────────────────────────────────────────────

const ChannelBar = ({ channel, count, maxCount }) => {
  const colors = CHANNEL_COLORS[channel] || CHANNEL_COLORS.widget;
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;

  return (
    <div className="flex items-center space-x-3 py-2">
      <span className="text-xs font-medium text-slate-600 w-16 text-right capitalize">
        {channel}
      </span>
      <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
        <div
          className={`${colors.bg} h-full rounded-full transition-all duration-500`}
          style={{ width: `${Math.max(pct, 1)}%` }}
        />
      </div>
      <span className="text-xs font-mono text-slate-500 w-16">
        {fmt(count)}
      </span>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────

const UsageDashboard = () => {
  const { token } = useAuth();

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [period, setPeriod] = useState("6m");
  const [loading, setLoading] = useState(false);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // Fetch facilities list
  useEffect(() => {
    const fetchFacilities = async () => {
      setFacilitiesLoading(true);
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const list = Array.isArray(json) ? json : json.facilities || [];
        setFacilities(list);
        if (list.length > 0 && !selectedFacility) {
          setSelectedFacility(list[0].facility_id || list[0].id || "");
        }
      } catch (err) {
        console.error("Failed to fetch facilities:", err);
      }
      setFacilitiesLoading(false);
    };
    if (token) fetchFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch usage data
  const fetchUsage = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${AUTH_URL}/analytics/facility-usage?facility_id=${encodeURIComponent(selectedFacility)}&period=${period}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(Array.isArray(json) ? json[0] || {} : json);
    } catch (err) {
      console.error("Usage fetch failed:", err);
      setError(err.message);
      setData(null);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility, period, token]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Derived values
  const summary = data?.summary || {};
  const monthly = data?.monthly || [];
  const channels = data?.channels || [];
  const maxChannelCount = Math.max(...channels.map((c) => c.count || 0), 1);

  const totalCost = calcCost(summary.total_tokens_in, summary.total_tokens_out);

  // Cost projection: average monthly cost extrapolated
  const monthlyAggregated = {};
  monthly.forEach((row) => {
    if (!monthlyAggregated[row.month]) {
      monthlyAggregated[row.month] = {
        tokens_in: 0,
        tokens_out: 0,
      };
    }
    monthlyAggregated[row.month].tokens_in += row.total_tokens_in || 0;
    monthlyAggregated[row.month].tokens_out += row.total_tokens_out || 0;
  });

  const monthKeys = Object.keys(monthlyAggregated);
  const projectedMonthlyCost =
    monthKeys.length > 0
      ? monthKeys.reduce(
          (sum, m) =>
            sum +
            calcCost(
              monthlyAggregated[m].tokens_in,
              monthlyAggregated[m].tokens_out,
            ),
          0,
        ) / monthKeys.length
      : 0;

  // Aggregate monthly rows by month for the trend table
  const trendRows = {};
  monthly.forEach((row) => {
    if (!trendRows[row.month]) {
      trendRows[row.month] = {
        month: row.month,
        queries: 0,
        sessions: 0,
        tokens_in: 0,
        tokens_out: 0,
        response_sum: 0,
        response_count: 0,
      };
    }
    const t = trendRows[row.month];
    t.queries += row.total_queries || 0;
    t.sessions += row.unique_sessions || 0;
    t.tokens_in += row.total_tokens_in || 0;
    t.tokens_out += row.total_tokens_out || 0;
    if (row.avg_response_ms != null) {
      t.response_sum += (row.avg_response_ms || 0) * (row.total_queries || 1);
      t.response_count += row.total_queries || 1;
    }
  });
  const sortedTrend = Object.values(trendRows).sort((a, b) =>
    b.month.localeCompare(a.month),
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-slate-800">
            Usage Dashboard
          </h1>
        </div>
        <button
          onClick={fetchUsage}
          disabled={loading || !selectedFacility}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">Filters</span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {/* Facility Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Facility
            </label>
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              disabled={facilitiesLoading}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {facilitiesLoading && (
                <option value="">Loading facilities...</option>
              )}
              {!facilitiesLoading && facilities.length === 0 && (
                <option value="">No facilities found</option>
              )}
              {facilities.map((f) => (
                <option
                  key={f.facility_id || f.id}
                  value={f.facility_id || f.id}
                >
                  {f.display_name ||
                    f.facility_name ||
                    f.name ||
                    f.facility_id ||
                    f.id}
                </option>
              ))}
            </select>
          </div>

          {/* Period Selector */}
          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Period
            </label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          Failed to load usage data: {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-5 h-5 text-slate-400 animate-spin mr-2" />
          <span className="text-sm text-slate-500">Loading usage data...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !data && selectedFacility && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
          <Activity className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No usage data available</p>
          <p className="text-xs text-slate-400 mt-1">
            Select a facility and period to view usage metrics.
          </p>
        </div>
      )}

      {/* Dashboard Content */}
      {!loading && data && (
        <>
          {/* Metric Cards — 3x2 Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard
              icon={Hash}
              iconColor="text-blue-500"
              label="Total Queries"
              value={fmt(summary.total_queries)}
            />
            <MetricCard
              icon={MessageSquare}
              iconColor="text-green-500"
              label="Unique Sessions"
              value={fmt(summary.unique_sessions)}
            />
            <MetricCard
              icon={Zap}
              iconColor="text-amber-500"
              label="Tokens In"
              value={fmt(summary.total_tokens_in)}
            />
            <MetricCard
              icon={Zap}
              iconColor="text-purple-500"
              label="Tokens Out"
              value={fmt(summary.total_tokens_out)}
            />
            <MetricCard
              icon={Clock}
              iconColor="text-slate-500"
              label="Avg Response Time"
              value={
                summary.avg_response_ms != null
                  ? `${fmt(Math.round(summary.avg_response_ms))} ms`
                  : "-- ms"
              }
            />
            <MetricCard
              icon={DollarSign}
              iconColor="text-emerald-500"
              label="Estimated Cost"
              value={fmtCost(totalCost)}
              subtext="gpt-4o-mini pricing"
            />
          </div>

          {/* Cost Projection */}
          {monthKeys.length > 0 && (
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg border border-slate-200 p-4 flex items-center space-x-3">
              <TrendingUp className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Cost Projection
                </span>
                <span className="text-sm text-slate-500 ml-2">
                  At current rate, monthly cost:{" "}
                  <span className="font-semibold text-slate-800">
                    {fmtCost(projectedMonthlyCost)}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Monthly Trend Table */}
          {sortedTrend.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center space-x-2">
                <Activity className="w-4 h-4 text-blue-500" />
                <h2 className="text-sm font-semibold text-slate-800">
                  Monthly Trend
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Month
                      </th>
                      <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Queries
                      </th>
                      <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Sessions
                      </th>
                      <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Tokens In
                      </th>
                      <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Tokens Out
                      </th>
                      <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Avg Response
                      </th>
                      <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Est. Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedTrend.map((row) => {
                      const avgMs =
                        row.response_count > 0
                          ? Math.round(row.response_sum / row.response_count)
                          : null;
                      const rowCost = calcCost(row.tokens_in, row.tokens_out);
                      return (
                        <tr
                          key={row.month}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-5 py-3 font-medium text-slate-700">
                            {row.month}
                          </td>
                          <td className="px-5 py-3 text-right text-slate-600 font-mono">
                            {fmt(row.queries)}
                          </td>
                          <td className="px-5 py-3 text-right text-slate-600 font-mono">
                            {fmt(row.sessions)}
                          </td>
                          <td className="px-5 py-3 text-right text-slate-600 font-mono">
                            {fmt(row.tokens_in)}
                          </td>
                          <td className="px-5 py-3 text-right text-slate-600 font-mono">
                            {fmt(row.tokens_out)}
                          </td>
                          <td className="px-5 py-3 text-right text-slate-600 font-mono">
                            {avgMs != null ? `${fmt(avgMs)} ms` : "--"}
                          </td>
                          <td className="px-5 py-3 text-right text-slate-600 font-mono">
                            {fmtCost(rowCost)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Channel Breakdown */}
          {channels.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
              <div className="flex items-center space-x-2 mb-4">
                <BarChart3 className="w-4 h-4 text-purple-500" />
                <h2 className="text-sm font-semibold text-slate-800">
                  Channel Breakdown
                </h2>
              </div>
              <div className="space-y-1">
                {channels
                  .sort((a, b) => (b.count || 0) - (a.count || 0))
                  .map((ch) => (
                    <ChannelBar
                      key={ch.channel}
                      channel={ch.channel}
                      count={ch.count || 0}
                      maxCount={maxChannelCount}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Empty channel state */}
          {channels.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
              <BarChart3 className="w-6 h-6 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                No channel breakdown data available
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UsageDashboard;
